import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';
import { Spinner } from 'components/Spinner/spinner';
import YesNoModal from 'components/Modals/yesNoModal';
import { Wrapper, Container, Button } from 'user/components';
import {
  InfoAndScoreContainer,
  InfoAndScoreBox,
  Heading,
  HeadingUnderLine,
  LabelHeading,
  LabelValue,
  ScoreBoxContainer,
  GaugeAndPointerContainer,
  GaugeImage,
  PointerImage,
  ScoreDescriptionContainer,
  FlexContainer,
} from '../../containers/ReportPage/styles';
import {
  ButtonContainer,
  InfoLabelsContainer,
  RatingContainer,
  BestScoreContainer,
  SubmissionContainer,
  MatchTable,
  TableTitle,
  TableRow,
  SubmissionInfoLabelsContainer,
  CodeContainer,
} from './styles';

export default React.memo(() => {
  const history = useHistory();
  const { accountId, contestId } = useParams();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [showSubmissionInfo, setShowSubmissionInfo] = useState(false);
  const [submissionInfo, setSubmissionInfo] = useState({});

  const { getParticipantProfileForCMS, archiveSubmission, resetUpdatedIds } = useActions(actions);

  let { candidateInfo, isSubmissionArchived, loading, error } = useSelector((state) => state.CMSCandidateInfoReducer);

  useEffect(() => {
    getParticipantProfileForCMS(accountId, contestId);
  }, [getParticipantProfileForCMS, accountId, contestId]);

  useEffect(() => {
    if (isSubmissionArchived) {
      toast.success('Submission archived successfully');
      resetUpdatedIds();
      history.goBack();
    }
  }, [isSubmissionArchived, resetUpdatedIds, history]);

  const executeScroll = () => {
    let element = document.getElementById('submissionInfo');
    let headerOffset = 60;
    let elementPosition = element.getBoundingClientRect().top;
    let offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (submissionInfo.code) executeScroll();
  }, [submissionInfo]);

  useEffect(() => {
    if (!loading && error) toast.error(error);
  }, [loading, error]);

  const renderRatingBox = () => <RatingContainer>{Math.round(candidateInfo.bestScore)}</RatingContainer>;

  const renderGaugeBox = () => (
    <ScoreBoxContainer>
      <GaugeAndPointerContainer>
        <GaugeImage src={Images.gaugeModal} />
        <PointerImage angle={Math.round(candidateInfo.percent) * (180 / 100)} />
      </GaugeAndPointerContainer>
      <ScoreDescriptionContainer>
        <LabelValue>Better than {candidateInfo.percent}% of the reported results</LabelValue>
        <BestScoreContainer>
          <LabelHeading>Best Score:&nbsp;</LabelHeading>
          <LabelValue>{Math.round(candidateInfo.bestScore)}</LabelValue>
        </BestScoreContainer>
      </ScoreDescriptionContainer>
    </ScoreBoxContainer>
  );

  const renderMatchesList = () => {
    return candidateInfo?.submissions?.map((submission, index) => {
      return (
        <TableRow
          key={index}
          hasHoverCursor
          isGreyRow={index % 2 === 0}
          onClick={() => {
            setShowSubmissionInfo(true);
            setSubmissionInfo(submission);
          }}
        >
          <TableTitle className="col-4">{moment(submission.submittedAt).format('DD/MM/YYYY, H:mm')}</TableTitle>
          <TableTitle className="col-4">
            {candidateInfo.isTournament ? Math.round(submission.rating ?? 0) : submission.score}
          </TableTitle>
          <TableTitle className="col-4">{submission.languageVersion}</TableTitle>
        </TableRow>
      );
    });
  };

  const renderTable = () => (
    <>
      <MatchTable>
        <TableTitle isHead className="col-4">
          Date
        </TableTitle>
        <TableTitle isHead className="col-4">
          {candidateInfo.isTournament ? 'Rating' : 'Score'}
        </TableTitle>
        <TableTitle isHead className="col-4">
          Language
        </TableTitle>
      </MatchTable>
      {renderMatchesList()}
    </>
  );

  const renderSubmissionInfo = () => (
    <SubmissionContainer id="submissionInfo">
      <Heading>SUBMISSION INFO</Heading>
      <HeadingUnderLine />
      <SubmissionInfoLabelsContainer>
        <LabelHeading>{candidateInfo.isTournament ? 'Rating' : 'Score'}: &nbsp;</LabelHeading>
        <LabelValue>{candidateInfo.isTournament ? Math.round(submissionInfo.rating) : submissionInfo.score}</LabelValue>
      </SubmissionInfoLabelsContainer>
      {submissionInfo.startedAt && (
        <SubmissionInfoLabelsContainer>
          <LabelHeading>Started at: &nbsp;</LabelHeading>
          <LabelValue>{moment(submissionInfo.startedAt).format('DD/MM/YYYY, H:mm')}</LabelValue>
        </SubmissionInfoLabelsContainer>
      )}
      {submissionInfo.endedAt && (
        <SubmissionInfoLabelsContainer>
          <LabelHeading>Ended at: &nbsp;</LabelHeading>
          <LabelValue>{moment(submissionInfo.endedAt).format('DD/MM/YYYY, H:mm')}</LabelValue>
        </SubmissionInfoLabelsContainer>
      )}
      <SubmissionInfoLabelsContainer>
        <LabelHeading>Code written in {submissionInfo.languageVersion}</LabelHeading>
      </SubmissionInfoLabelsContainer>
      <CodeContainer>
        <SyntaxHighlighter
          // The library require uses a different name for c# then we do in editorNames
          language={submissionInfo.language === 'c_sharp' ? 'csharp' : submissionInfo.language}
          style={a11yLight}
        >
          {submissionInfo.code}
        </SyntaxHighlighter>
      </CodeContainer>
    </SubmissionContainer>
  );

  const renderTestCases = () => (
    <SubmissionContainer>
      <Heading>TEST CASES</Heading>
      <HeadingUnderLine />
      <MatchTable>
        <TableTitle isHead makeLeft className="col-8">
          Name
        </TableTitle>
        <TableTitle isHead className="col-4">
          Score
        </TableTitle>
      </MatchTable>
      {submissionInfo?.levelNames?.map((level, index) => {
        return (
          <TableRow key={index} isGreyRow={index % 2 === 0}>
            <TableTitle makeLeft className="col-8">
              {level}
            </TableTitle>
            <TableTitle className="col-4">{submissionInfo.levelScores[index]}</TableTitle>
          </TableRow>
        );
      })}
    </SubmissionContainer>
  );

  const renderInfoBox = () => (
    <InfoAndScoreContainer>
      <InfoAndScoreBox>
        <Heading>INFO</Heading>
        <HeadingUnderLine />
        <InfoLabelsContainer>
          <LabelHeading>Name: &nbsp;</LabelHeading>
          <LabelValue>{candidateInfo?.account?.name}</LabelValue>
        </InfoLabelsContainer>
        <InfoLabelsContainer>
          <LabelHeading>Email: &nbsp;</LabelHeading>
          <LabelValue>{candidateInfo?.account?.email}</LabelValue>
        </InfoLabelsContainer>
        {candidateInfo?.account?.phone && (
          <InfoLabelsContainer>
            <LabelHeading>Phone: &nbsp;</LabelHeading>
            <LabelValue>{candidateInfo.account.phone}</LabelValue>
          </InfoLabelsContainer>
        )}
        {candidateInfo?.account?.city && (
          <InfoLabelsContainer>
            <LabelHeading>City: &nbsp;</LabelHeading>
            <LabelValue>{candidateInfo.account.city}</LabelValue>
          </InfoLabelsContainer>
        )}
        {candidateInfo?.account?.linkedIn && (
          <InfoLabelsContainer>
            <LabelHeading>LinkedIn: &nbsp;</LabelHeading>
            <LabelValue>{candidateInfo.account.linkedIn}</LabelValue>
          </InfoLabelsContainer>
        )}
        {candidateInfo?.account?.github && (
          <InfoLabelsContainer>
            <LabelHeading>GitHub: &nbsp;</LabelHeading>
            <LabelValue>{candidateInfo.account.github}</LabelValue>
          </InfoLabelsContainer>
        )}
      </InfoAndScoreBox>
      <InfoAndScoreBox>
        <Heading>{candidateInfo.isTournament ? 'RATING' : 'SCORE'}</Heading>
        <HeadingUnderLine />
        {candidateInfo.isTournament && candidateInfo?.account?.name ? renderRatingBox() : renderGaugeBox()}
      </InfoAndScoreBox>
    </InfoAndScoreContainer>
  );

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100vh">
      {showConfirmationModal && (
        <YesNoModal
          modalInfo={modalInfo}
          confirm={(isConfirmed) => {
            if (isConfirmed) archiveSubmission(accountId, contestId, candidateInfo.isTournament);
            setModalInfo({});
            setShowConfirmationModal(false);
          }}
        />
      )}
      {loading && <Spinner />}
      {!loading && candidateInfo && (
        <Container column>
          <ButtonContainer>
            <Button small primaryBlue primaryGreenBorder margin="5px" onClick={() => history.goBack()}>
              <i className="fa fa-arrow-left"></i>&nbsp;Back
            </Button>
          </ButtonContainer>
          {renderInfoBox()}
          <SubmissionContainer>
            <FlexContainer>
              <Heading>SUBMISSIONS</Heading>
              <Button
                small
                primaryRed
                margin="5px"
                onClick={() => {
                  setShowConfirmationModal(true);
                  setModalInfo({
                    title: 'Confirmation',
                    body: `Do you want to archive the submission`,
                  });
                }}
              >
                <i className="fa fa-trash-o"></i>&nbsp;Archive
              </Button>
            </FlexContainer>
            <HeadingUnderLine />
            {renderTable()}
          </SubmissionContainer>
          {showSubmissionInfo && renderSubmissionInfo()}
          {showSubmissionInfo && submissionInfo?.levelNames?.length && renderTestCases()}
        </Container>
      )}
    </Wrapper>
  );
});
